/* workflowText — plain-language rendering of workflow JSON fragments for
 * the flow graph: expression cleanup, "key ← expression" mapping lines,
 * input/output summaries and on_error notes. No raw braces ever reach the
 * screen — every helper returns human-readable text. */

/** Strip engine prefixes from an expression (string or { $: "..." }). */
export function cleanExpr(v: any, max = 0): string {
    let s: string;
    if (v != null && typeof v === 'object' && typeof v.$ === 'string') {
        s = v.$;
    } else {
        s = String(v ?? '');
    }
    s = s
        .replace(/\$\.steps\./g, '')
        .replace(/\$\.input\./g, 'input.')
        .replace(/\$\.context\./g, 'ctx.')
        .replace(/\$\./g, '');
    if (max > 0 && s.length > max) return `${s.slice(0, max - 1)}…`;
    return s;
}

/** One mapping value → readable right-hand side (never raw JSON). */
export function exprText(v: any): string {
    if (v == null) return 'null';
    if (typeof v === 'string') {
        return v.startsWith('$.') ? cleanExpr(v) : `'${v}'`;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) return `[${v.map(exprText).join(', ')}]`;
    if (typeof v.$ === 'string') return cleanExpr(v);
    return mapLines(v).join('; ');
}

/** Mapping object → "key ← expression" lines; nested keys are dotted. */
export function mapLines(v: any, prefix = ''): string[] {
    if (v == null || typeof v !== 'object' || Array.isArray(v)
        || typeof v.$ === 'string') {
        return [`${prefix || 'value'} ← ${exprText(v)}`];
    }
    const out: string[] = [];
    for (const k of Object.keys(v)) {
        const val = v[k];
        const path = prefix ? `${prefix}.${k}` : k;
        if (val != null && typeof val === 'object' && !Array.isArray(val)
            && typeof val.$ !== 'string') {
            out.push(...mapLines(val, path));
        } else {
            out.push(`${path} ← ${exprText(val)}`);
        }
    }
    return out.length ? out : [`${prefix || 'value'} ← (empty)`];
}

interface FieldInfo {
    name: string;
    type: any;
    required: boolean;
}

function structFields(input: any): FieldInfo[] {
    if (input?.kind === 'struct' && Array.isArray(input.fields)) {
        return input.fields.map((f: any) => ({
            name: String(f?.name ?? '?'),
            type: f?.type,
            required: !!f?.required
        }));
    }
    if (input && typeof input === 'object' && !Array.isArray(input)) {
        return Object.keys(input).filter((k) => k !== 'kind').map((k) => ({
            name: k,
            type: input[k],
            required: false
        }));
    }
    return [];
}

function typeName(t: any): string {
    if (t == null) return 'any';
    if (t.kind === 'primitive') return String(t.name || 'any');
    if (t.kind === 'enum') return (t.values || []).join(' | ');
    if (t.kind === 'list') return 'list';
    return String(t.kind || 'any');
}

/** "pet_id*, pet_name*, note" — the START node's one-liner. */
export function inputSummary(input: any): string {
    const f = structFields(input);
    if (!f.length) return 'no inputs';
    return f.map((x) => (x.required ? `${x.name}*` : x.name)).join(', ');
}

/** Tooltip body: one "name — type, required" line per input field. */
export function inputLines(input: any): string {
    const f = structFields(input);
    if (!f.length) return 'This workflow takes no input.';
    return f.map((x) => `${x.name} — ${typeName(x.type)}`
        + (x.required ? ', required' : '')).join('\n');
}

/** "adoption_id, order_id, status" — the END node's one-liner. */
export function outputSummary(output: any): string {
    const keys = output && typeof output === 'object'
        ? Object.keys(output) : [];
    if (!keys.length) return 'last step output passes through';
    return keys.join(', ');
}

/** Tooltip body: "key ← expression" per output mapping entry. */
export function outputLines(output: any): string {
    const keys = output && typeof output === 'object'
        ? Object.keys(output) : [];
    if (!keys.length) {
        return 'No output mapping — the last step\'s output passes through.';
    }
    return keys.map((k) => `${k} ← ${exprText(output[k])}`).join('\n');
}

/** Plain-language note for a step's on_error policy (null = default). */
export function onErrorNote(policy: any): string | null {
    if (policy == null) return null;
    if (policy === 'stop') return 'On failure: the workflow stops here.';
    if (policy === 'continue') {
        return 'On failure: this step is skipped and the run continues.';
    }
    if (typeof policy === 'object' && policy.run) {
        return `On failure: runs ${String(policy.run)}, then the workflow `
            + 'stops.';
    }
    return 'On failure: unrecognized policy.';
}
